import { Wrench, WifiOff, RefreshCw, AlertTriangle } from 'lucide-react';
import { Button } from '@ridemaadi/ui';
import { Logo } from './Logo';

export type SystemErrorReason = 'maintenance' | 'unexpected' | 'offline' | 'updating';

const COPY: Record<
  SystemErrorReason,
  { title: string; defaultMessage: string; icon: typeof AlertTriangle }
> = {
  maintenance: {
    title: 'Under maintenance',
    defaultMessage: 'RideMaadi is temporarily unavailable while we perform maintenance. Please check back soon.',
    icon: Wrench,
  },
  unexpected: {
    title: 'Something went wrong',
    defaultMessage: 'An unexpected error occurred. Our team has been notified.',
    icon: AlertTriangle,
  },
  offline: {
    title: 'Service unavailable',
    defaultMessage: 'Unable to reach RideMaadi right now. Check your connection and try again.',
    icon: WifiOff,
  },
  updating: {
    title: 'Updating app…',
    defaultMessage: 'A new version was deployed. Reloading to fetch the latest files.',
    icon: RefreshCw,
  },
};

/** Unified fallback page for maintenance mode and unexpected errors. */
export function SystemErrorPage({
  reason,
  message,
  onRetry,
}: {
  reason: SystemErrorReason;
  message?: string;
  onRetry?: () => void;
}) {
  const copy = COPY[reason];
  const Icon = copy.icon;
  const showRetry = reason !== 'updating';

  return (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-6 overflow-x-hidden bg-bg px-6 py-10 text-center">
      <Logo variant="pageload" className="max-w-[220px]" />
      <div className="flex max-w-md flex-col items-center gap-3">
        <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/15 text-primary">
          <Icon className="h-7 w-7" aria-hidden />
        </div>
        <h1 className="text-xl font-bold text-content">{copy.title}</h1>
        <p className="text-sm leading-relaxed text-muted">{message ?? copy.defaultMessage}</p>
      </div>
      {showRetry && (
        <Button
          type="button"
          onClick={onRetry ?? (() => window.location.reload())}
          className="min-w-[160px]"
        >
          Try again
        </Button>
      )}
    </div>
  );
}
